import React, { useState, useEffect } from "react";
import { usersAPI, authAPI, internalContactsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Users,
  UserPlus,
  Trash2,
  Loader2,
  Shield,
  User,
  Edit,
  Building2,
} from "lucide-react";

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [internalContacts, setInternalContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetType, setSheetType] = useState("user"); // "user" or "internal"
  const [editingInternal, setEditingInternal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState("user"); // "user" or "internal"
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "operatore",
  });

  const [internalFormData, setInternalFormData] = useState({
    name: "",
    email: "",
    department: "",
    notes: "",
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, internalRes] = await Promise.all([
        usersAPI.getAll(),
        internalContactsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setInternalContacts(internalRes.data);
    } catch (error) {
      showMessage("Errore nel caricamento", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name || !formData.password) {
      showMessage("Compila tutti i campi obbligatori", "error");
      return;
    }
    setSaving(true);
    try {
      await authAPI.register(formData);
      showMessage("Utente creato");
      setShowSheet(false);
      setFormData({ email: "", name: "", password: "", role: "operatore" });
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleInternalSubmit = async (e) => {
    e.preventDefault();
    if (!internalFormData.name) {
      showMessage("Il nome e obbligatorio", "error");
      return;
    }
    setSaving(true);
    try {
      if (editingInternal) {
        await internalContactsAPI.update(editingInternal.id, internalFormData);
        showMessage("Contatto interno aggiornato");
      } else {
        await internalContactsAPI.create(internalFormData);
        showMessage("Contatto interno creato");
      }
      setShowSheet(false);
      setEditingInternal(null);
      setInternalFormData({ name: "", email: "", department: "", notes: "" });
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (userId, data) => {
    try {
      await usersAPI.update(userId, data);
      showMessage("Utente aggiornato");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'aggiornamento", "error");
    }
  };

  const handleDeleteClick = (id, type = "user") => {
    setDeleteId(id);
    setDeleteType(type);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      if (deleteType === "internal") {
        await internalContactsAPI.delete(deleteId);
        showMessage("Contatto interno eliminato");
      } else {
        await usersAPI.delete(deleteId);
        showMessage("Utente eliminato");
      }
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteId(null);
      setShowDeleteDialog(false);
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    await handleUpdate(userId, { is_active: !isActive });
  };

  const changeUserRole = async (userId, newRole) => {
    await handleUpdate(userId, { role: newRole });
  };

  const openInternalEdit = (contact) => {
    setEditingInternal(contact);
    setInternalFormData({
      name: contact.name,
      email: contact.email || "",
      department: contact.department || "",
      notes: contact.notes || "",
    });
    setSheetType("internal");
    setShowSheet(true);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogDescription>
              {deleteType === "internal"
                ? "Sei sicuro di voler eliminare questo contatto interno?"
                : "Sei sicuro di voler eliminare questo utente?"}
              {" "}Questa azione non puo essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-user">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Gestione Utenti
          </h1>
          <p className="text-muted-foreground mt-1">
            Utenti di sistema e contatti interni
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users">
        <TabsList className="bg-muted">
          <TabsTrigger value="users" data-testid="tab-users" className="flex items-center gap-2">
            <Users size={16} />
            Utenti ({users.length})
          </TabsTrigger>
          <TabsTrigger value="internal" data-testid="tab-internal" className="flex items-center gap-2">
            <Building2 size={16} />
            Contatti Interni ({internalContacts.length})
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Users size={20} className="text-[#2D7BB7]" />
                Utenti Registrati
              </CardTitle>
              <Button
                onClick={() => { setSheetType("user"); setShowSheet(true); }}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
                data-testid="add-user-btn"
              >
                <UserPlus className="mr-2" size={18} />
                Nuovo Utente
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-20 bg-muted rounded-lg" />
                  ))}
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nessun utente registrato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                      data-testid={`user-item-${user.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#2D7BB7] flex items-center justify-center text-white font-bold text-lg">
                          {user.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-foreground font-medium flex items-center gap-2">
                            {user.name}
                            {user.id === currentUser?.id && (
                              <Badge className="bg-[#2D7BB7]/10 text-[#2D7BB7] border-[#2D7BB7]/20 text-xs">
                                Tu
                              </Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Registrato il {formatDate(user.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <Select
                          value={user.role}
                          onValueChange={(val) => changeUserRole(user.id, val)}
                          disabled={user.id === currentUser?.id}
                        >
                          <SelectTrigger className="w-32 bg-background border-input" data-testid={`role-select-${user.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="operatore">Operatore</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => toggleUserStatus(user.id, user.is_active)}
                            disabled={user.id === currentUser?.id}
                            data-testid={`status-switch-${user.id}`}
                          />
                          <span className={`text-sm ${user.is_active ? "text-emerald-600" : "text-red-600"}`}>
                            {user.is_active ? "Attivo" : "Disattivo"}
                          </span>
                        </div>
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(user.id, "user")}
                            className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Internal Contacts Tab */}
        <TabsContent value="internal">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Building2 size={20} className="text-[#2D7BB7]" />
                Contatti Interni
              </CardTitle>
              <Button
                onClick={() => {
                  setSheetType("internal");
                  setEditingInternal(null);
                  setInternalFormData({ name: "", email: "", department: "", notes: "" });
                  setShowSheet(true);
                }}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
                data-testid="add-internal-btn"
              >
                <UserPlus className="mr-2" size={18} />
                Nuovo Contatto Interno
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton key={i} className="h-20 bg-muted rounded-lg" />
                  ))}
                </div>
              ) : internalContacts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nessun contatto interno</p>
                  <p className="text-sm mt-1">Aggiungi dipendenti o collaboratori che non hanno accesso al sistema</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {internalContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                      data-testid={`internal-item-${contact.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-amber-500/80 flex items-center justify-center text-white font-bold text-lg">
                          {contact.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-foreground font-medium">{contact.name}</p>
                          {contact.email && (
                            <p className="text-sm text-muted-foreground">{contact.email}</p>
                          )}
                          {contact.department && (
                            <p className="text-xs text-muted-foreground">
                              Reparto: {contact.department}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openInternalEdit(contact)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`edit-internal-${contact.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(contact.id, "internal")}
                          className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-internal-${contact.id}`}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Sheet */}
      <Sheet open={showSheet} onOpenChange={(open) => {
        setShowSheet(open);
        if (!open) { setEditingInternal(null); }
      }}>
        <SheetContent className="bg-card border-border">
          <SheetHeader>
            <SheetTitle className="text-foreground">
              {sheetType === "internal"
                ? editingInternal ? "Modifica Contatto Interno" : "Nuovo Contatto Interno"
                : "Nuovo Utente"}
            </SheetTitle>
          </SheetHeader>

          {sheetType === "user" ? (
            <form onSubmit={handleSubmit} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label className="text-foreground">Nome *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  className="bg-background border-input text-foreground"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@splendorsud.it"
                  className="bg-background border-input text-foreground"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-background border-input text-foreground"
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Ruolo</Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operatore">Operatore</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <SheetFooter className="mt-6">
                <SheetClose asChild>
                  <Button type="button" variant="outline" className="border-border text-foreground">
                    Annulla
                  </Button>
                </SheetClose>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                  data-testid="save-user-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea Utente"}
                </Button>
              </SheetFooter>
            </form>
          ) : (
            <form onSubmit={handleInternalSubmit} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label className="text-foreground">Nome *</Label>
                <Input
                  value={internalFormData.name}
                  onChange={(e) => setInternalFormData({ ...internalFormData, name: e.target.value })}
                  placeholder="Nome completo"
                  className="bg-background border-input text-foreground"
                  data-testid="internal-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Email</Label>
                <Input
                  type="email"
                  value={internalFormData.email}
                  onChange={(e) => setInternalFormData({ ...internalFormData, email: e.target.value })}
                  placeholder="email@splendorsud.it"
                  className="bg-background border-input text-foreground"
                  data-testid="internal-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Reparto</Label>
                <Input
                  value={internalFormData.department}
                  onChange={(e) => setInternalFormData({ ...internalFormData, department: e.target.value })}
                  placeholder="es. Amministrazione, Operativo..."
                  className="bg-background border-input text-foreground"
                  data-testid="internal-department-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Note</Label>
                <Input
                  value={internalFormData.notes}
                  onChange={(e) => setInternalFormData({ ...internalFormData, notes: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="bg-background border-input text-foreground"
                />
              </div>
              <SheetFooter className="mt-6">
                <SheetClose asChild>
                  <Button type="button" variant="outline" className="border-border text-foreground">
                    Annulla
                  </Button>
                </SheetClose>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                  data-testid="save-internal-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingInternal ? "Salva" : "Crea"}
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
